import { FunctionComponent, useMemo } from 'react';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import { formatearCapacidad } from '../utils/estadisticasGdPalette';
import {
  formatearTickEsCl,
  vdrChartSx,
  vdrLegendLabelStyle,
  vdrOverlaySlotProps,
  vdrTooltipPopperSx
} from './vdrChartStyle';

// Ticks y encabezado del tooltip como en el rango temporal: 'YYYY-MM' → 'ene 2010'.
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const formatearMesCorto = (yyyymm: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(yyyymm));
  const mes = match ? MESES_CORTOS[parseInt(match[2], 10) - 1] : undefined;
  return match && mes ? `${mes} ${match[1]}` : String(yyyymm);
};

// Capas apiladas: un filo blanco fino entre capas en vez de la línea del color de la serie.
const STACKED_AREA_SX = {
  ...vdrChartSx,
  '& .MuiLineElement-root': { stroke: '#ffffff', strokeWidth: 1 }
};

type PuntoSerie = {
  fecha: string;
  totalProyectos: number;
  [key: string]: string | number;
};

const StackedAreaGd: FunctionComponent<{
  serie: PuntoSerie[];
  tecnologias: string[];
  colorPorTecnologia: Map<string, string>;
  unidadCapacidad: 'MW' | 'kW';
  metrica: 'capacidad' | 'cantidad';
  desde: string;
  hasta: string;
  altura?: number;
}> = ({
  serie,
  tecnologias,
  colorPorTecnologia,
  unidadCapacidad,
  metrica,
  desde,
  hasta,
  altura = 320
}) => {
  const serieFiltrada = useMemo(() => {
    return serie.filter((p) => p.fecha >= desde && p.fecha <= hasta);
  }, [serie, desde, hasta]);

  const fechas = useMemo(() => serieFiltrada.map((p) => p.fecha), [serieFiltrada]);

  const valorPorTecnologia = (punto: PuntoSerie, tec: string): number => {
    if (metrica === 'capacidad') {
      const v = punto[tec];
      return typeof v === 'number' ? v : Number(v) || 0;
    }
    const v = punto[`${tec}__count`];
    return typeof v === 'number' ? v : Number(v) || 0;
  };

  const series = useMemo(
    () =>
      tecnologias.map((tec) => ({
        data: serieFiltrada.map((p) => valorPorTecnologia(p, tec)),
        label: tec,
        area: true,
        stack: 'total',
        showMark: false,
        color: colorPorTecnologia.get(tec) ?? '#9e9e9e'
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tecnologias, serieFiltrada, metrica, colorPorTecnologia]
  );

  const yAxisLabel =
    metrica === 'capacidad'
      ? `Capacidad acumulada (${unidadCapacidad})`
      : 'Cantidad acumulada de proyectos';

  const valueFormatter = (v: number | null) => {
    if (v === null || !Number.isFinite(v)) return '—';
    return metrica === 'capacidad'
      ? formatearCapacidad(v, unidadCapacidad)
      : `${Math.round(v).toLocaleString('es-CL')} proy.`;
  };

  if (fechas.length === 0 || tecnologias.length === 0) {
    return (
      <div className='stacked-area-gd-status' style={{ minHeight: altura }}>
        Sin datos en el rango seleccionado.
      </div>
    );
  }

  return (
    <div className='stacked-area-gd-container' style={{ height: altura }}>
      <MuiLineChart
        xAxis={[
          {
            data: fechas,
            scaleType: 'band',
            valueFormatter: formatearMesCorto,
            tickInterval: (_, idx) => idx % Math.ceil(fechas.length / 12) === 0
          }
        ]}
        yAxis={[
          { label: yAxisLabel, labelStyle: { fontSize: 13 }, valueFormatter: formatearTickEsCl }
        ]}
        series={series.map((s) => ({ ...s, valueFormatter }))}
        height={altura}
        margin={{ top: 40, bottom: 128, left: 88, right: 16 }}
        grid={{ horizontal: true }}
        sx={STACKED_AREA_SX}
        leftAxis={{
          slotProps: {
            axisLabel: { x: -72 }
          }
        }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: { top: 4, bottom: 8, left: 8, right: 8 },
            itemGap: 12,
            markGap: 6,
            itemMarkWidth: 10,
            itemMarkHeight: 10,
            labelStyle: { ...vdrLegendLabelStyle, fontSize: 11.5 }
          },
          popper: { sx: vdrTooltipPopperSx },
          ...vdrOverlaySlotProps
        }}
      />
    </div>
  );
};

export default StackedAreaGd;
